import React from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  Button
} from '@mui/material';
import { Email, Phone } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const termsSections = [
  {
    heading: 'Agreement to Terms',
    items: [
      'By accessing or using the Zaror Sports website you agree to be bound by these Terms of Service and all applicable laws. If you do not agree, please refrain from using our services.'
    ]
  },
  {
    heading: 'Products and Services',
    items: [
      'All custom kit designs are produced to order. Colours may vary slightly between digital mock-ups and finished garments due to fabrication processes.',
      'We reserve the right to discontinue or modify products, pricing, or services without prior notice.'
    ]
  },
  {
    heading: 'Orders and Payments',
    items: [
      'Orders are confirmed once payment is received or an approved purchase order is supplied. Lead times commence after design approval and payment confirmation.',
      'Any additional charges arising from design amendments or expedited delivery will be invoiced accordingly.'
    ]
  },
  {
    heading: 'Intellectual Property',
    items: [
      'All designs, branding, and associated materials created by Zaror Sports remain our intellectual property unless expressly transferred in writing.',
      'Customers confirm they own or have permission to use any logos or graphics supplied for customisation.'
    ]
  },
  {
    heading: 'Returns and Refunds',
    items: [
      'Due to the bespoke nature of our products, returns are not accepted unless the goods are faulty or differ materially from the approved proof.',
      'Any issues must be reported within 7 days of receipt so that we can investigate and, where appropriate, arrange replacements or refunds.'
    ]
  },
  {
    heading: 'Limitation of Liability',
    items: [
      'Zaror Sports is not liable for indirect, incidental, or consequential damages arising from the use of our products or services.',
      'Our total liability is limited to the amount paid for the specific order giving rise to the claim.'
    ]
  },
  {
    heading: 'Governing Law',
    items: [
      'These Terms of Service are governed by and construed in accordance with the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.'
    ]
  }
];

const ContactCTA = () => (
  <Box
    sx={{
      backgroundImage:
        'linear-gradient(135deg, rgba(0,0,0,0.88) 0%, rgba(34,34,34,0.9) 100%)',
      color: 'white',
      py: { xs: 8, md: 10 },
      mt: 8
    }}
  >
    <Container maxWidth="lg">
      <Typography variant="h6" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
        Contacts
      </Typography>
      <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 4 }}>
        For any inquiries please email or call us
      </Typography>
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Email sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="mailto:info@zaror.co.uk"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                info@zaror.co.uk
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Phone sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="tel:08006351020"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                0800 635 1020
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Phone sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="tel:01216901020"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                0121 690 1020
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={6}
            sx={{
              p: { xs: 4, md: 5 },
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Need help with an order?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
              Get in touch with our team for assistance with artwork, approvals, or delivery timelines.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                color="secondary"
                sx={{ fontWeight: 600, borderRadius: 2 }}
              >
                Contact Us
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const TermsOfService = () => {
  return (
    <Box>
      <Box sx={{ backgroundColor: '#111827', color: 'white', py: { xs: 10, md: 12 } }}>
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
            <Typography variant="h4" sx={{ letterSpacing: 6, textTransform: 'uppercase' }}>
              Terms of Service
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              sx={{ fontWeight: 700, textAlign: { xs: 'center', md: 'left' } }}
            >
              The rules for working with Zaror Sports
            </Typography>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 720,
                opacity: 0.85,
                textAlign: { xs: 'center', md: 'left' }
              }}
            >
              These terms explain how we provide our bespoke sportswear services, the responsibilities of both parties, and the policies that keep your orders on track.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={4}>
          {termsSections.map((section) => (
            <Paper key={section.heading} elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                {section.heading}
              </Typography>
              <Stack spacing={1.5}>
                {section.items.map((item, index) => (
                  <Typography key={index} variant="body1" color="text.secondary">
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Container>

      <ContactCTA />

      <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default TermsOfService;
